import { useMemo } from "react";
import { type ChatListParams, useChats } from "@/hooks/useChats";
import { dedupeByDocumentId } from "@/shared/utils";
import type { TabValue } from "@/features/chat/stores/useChatsTabs";
import type { ChatScopeKeyParams } from "@/features/chat/utils/chatScope";
import { SEARCH_ATTENDANT_FILTERS } from "@/features/chat/utils/chatTabs";
import type { ChatWithLastAttendance, DocID } from "@/types";
import { useClosedChats } from "@/features/chat/hooks/useClosedChats";

/** Uma lista da tela de conversas (uma aba ou a busca). */
export type ChatTabList = {
  chats: ChatWithLastAttendance[];
  /** `pagination.total` da 1ª página: o contador da aba. `null` carregando. */
  total: number | null;
  isPending: boolean;
  isError: boolean;
  error: unknown;
  isFetching: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  refetch: () => void;
};

type UseChatTabListsParams = {
  sectorId?: DocID | null;
  tagIds: DocID[];
  /** Recorte das abas humanas; Com a IA e a busca não o usam. */
  scope: ChatScopeKeyParams;
  /** Texto da busca já debounced; vazio = sem busca. */
  search: string;
  /** A lista de Encerradas está à vista: só então a query dela roda. */
  closedEnabled?: boolean;
  /** SA-1455: "Não lidas" em Em atendimento (o contador da aba acompanha). */
  unreadOnly?: boolean;
};

/**
 * As três listas que a tela monta com o estado inicial (recorte "Todas", sem
 * etiqueta, sem busca, sem "Não lidas"): o boot as pré-carrega (SA-1452), e o
 * teste das fábricas garante que as chaves batem com as de `useChatTabLists`.
 */
export const DEFAULT_CHAT_TAB_LISTS: Omit<ChatListParams, "sectorId">[] = [
  { attendantFilters: ["human"], search: "", tagIds: [] },
  { attendantFilters: ["awaiting_human"], search: "", tagIds: [] },
  { attendantFilters: ["ai"], search: "", tagIds: [] },
];

const useChatList = (
  query: Pick<
    ReturnType<typeof useChats>,
    | "data"
    | "isPending"
    | "isError"
    | "error"
    | "isFetching"
    | "hasNextPage"
    | "fetchNextPage"
    | "refetch"
  >
): ChatTabList => {
  const { data, isPending, isError, error, isFetching, hasNextPage } = query;
  const { fetchNextPage, refetch } = query;
  const chats = useMemo(
    () => dedupeByDocumentId(data?.pages.flatMap((page) => page.data) ?? []),
    [data?.pages]
  );
  const total = data?.pages?.[0]?.meta?.pagination?.total ?? null;

  return useMemo(
    () => ({
      chats,
      total,
      isPending,
      isError,
      error,
      isFetching,
      hasNextPage,
      fetchNextPage: () => void fetchNextPage(),
      refetch: () => void refetch(),
    }),
    [
      chats,
      total,
      isPending,
      isError,
      error,
      isFetching,
      hasNextPage,
      fetchNextPage,
      refetch,
    ]
  );
};

/**
 * As listas da tela de conversas (SA-1368): uma query por aba, todas sempre
 * rodando (o contador de cada aba é o total dela, e a reconciliação das
 * transições precisa ver as três), e a da busca, que cruza as abas e só roda
 * com texto. Tags valem em todas; o recorte, só nas abas humanas.
 *
 * SA-1369: Encerradas é uma lista a mais (rota `/closed`, com tags e sem
 * recorte), que só roda com ela à vista.
 *
 * SA-1455: "Não lidas" troca os params da query de Em atendimento (nenhuma
 * query a mais); o contador da aba passa a ser o das não lidas.
 */
export function useChatTabLists({
  sectorId,
  tagIds,
  scope,
  search,
  closedEnabled = false,
  unreadOnly = false,
}: UseChatTabListsParams): {
  lists: Record<TabValue, ChatTabList>;
  searchList: ChatTabList;
} {
  const sector = sectorId ?? "";

  const human = useChatList(
    useChats(sector, ["human"], "", tagIds, scope, { unreadOnly })
  );
  const awaiting = useChatList(
    useChats(sector, ["awaiting_human"], "", tagIds, scope)
  );
  const ai = useChatList(useChats(sector, ["ai"], "", tagIds));
  const closed = useChatList(
    useClosedChats(sectorId, tagIds, { enabled: closedEnabled })
  );
  const searchList = useChatList(
    useChats(sector, SEARCH_ATTENDANT_FILTERS, search, tagIds, undefined, {
      enabled: search.trim().length > 0,
    })
  );

  const lists = useMemo(
    () => ({ human, awaiting_human: awaiting, ai, closed }),
    [human, awaiting, ai, closed]
  );

  return { lists, searchList };
}
